/*
 * Facturation des consommateurs depuis la todolist : un canal par membre (Accountable, e-mail, domiciliation).
 * - Toutes les factures partent vers Accountable : revenue@, ou expenses@ pour un décompte (montant négatif).
 * - Le membre reçoit la sienne selon son canal ; en domiciliation, l'e-mail vaut pré-notification
 *   (au moins 5 jours ouvrables avant le prélèvement) et la facture entre dans le fichier XML.
 * - Numéro figé à l'émission (billing_invoices), une seule facture par membre et par mois ;
 *   un nouvel essai régénère le PDF et bloque l'envoi si le montant a changé.
 */
import { supabase } from '../../lib/supabase';
import type { BillingChannel, TodoItem } from '../../../supabase/functions/_shared/crmTodo';
import { resolveBillingChannel } from '../../../supabase/functions/_shared/crmTodo';
import { ibanError, isValidBic, maskIban, normalizeBic, normalizeIban } from '../../../supabase/functions/_shared/sepa';
import type { CrmContact } from './types';
import { mandateState } from './types';
import { callFunction } from './api';
import { noRowError } from './session';
import type { InvoiceBillingInfo } from '../InvoiceTemplate';

export type BillingStatus = 'prepared' | 'sent' | 'to_collect' | 'collected' | 'rejected' | 'refunded';

export const STATUS_LABELS: Record<BillingStatus, string> = {
  prepared: 'Préparée',
  sent: 'Envoyée',
  to_collect: 'À encaisser',
  collected: 'Encaissée',
  rejected: 'Rejetée',
  refunded: 'Remboursée',
};

export interface BillingInvoice {
  id: string;
  entity_key: string;
  period: string;
  period_start: string;
  period_end: string;
  contact_id: string | null;
  sharing_operation_id: string | null;
  participant_ids: string[];
  company_number: string | null;
  title: string;
  channel: BillingChannel;
  invoice_number: string;
  amount: number | null;
  issue_date: string;
  document_kind: 'invoice' | 'statement' | null;
  accountable_target: 'revenue' | 'expense' | null;
  member_email: string | null;
  copy_to_member: boolean;
  accountable_sent_at: string | null;
  member_sent_at: string | null;
  collection_date: string | null;
  mandate_reference: string | null;
  mandate_signed_at: string | null;
  debtor_name: string | null;
  debtor_iban: string | null;
  debtor_bic: string | null;
  sequence_type: 'FRST' | 'RCUR' | null;
  sepa_batch_id: string | null;
  status: BillingStatus;
  status_date: string | null;
  last_error: string | null;
  created_at: string;
}

export interface SepaBatch {
  id: string;
  msg_id: string;
  format: string;
  collection_date: string;
  import_deadline: string | null;
  tx_count: number;
  total: number;
  frst_count: number;
  rcur_count: number;
  file_path: string;
  file_name: string;
  created_by: string | null;
  created_at: string;
}

const toNumber = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
const normalizeInvoice = (r: Record<string, unknown>): BillingInvoice => ({
  ...(r as unknown as BillingInvoice),
  amount: toNumber(r.amount),
  participant_ids: Array.isArray(r.participant_ids) ? (r.participant_ids as string[]) : [],
});

export const invoiceKey = (entityKey: string, period: string) => `${entityKey}|${period}`;
/** Mois de la facture : celui de la tâche ; prosumer sur plusieurs mois (« AAAA-MM_AAAA-MM ») : le dernier mois (numéro préfixe-MM-AA). */
export const billingPeriodOf = (item: Pick<TodoItem, 'period' | 'periodEnd'>) => (/^\d{4}-\d{2}$/.test(item.period) ? item.period : item.periodEnd);
export const itemInvoiceKey = (item: Pick<TodoItem, 'ref' | 'period' | 'periodEnd'>) => invoiceKey(item.ref, billingPeriodOf(item));

/** Une destination au moins est partie : canal, montant et dates sont figés. */
export const isStarted = (r: BillingInvoice | null | undefined) => !!r && (!!r.accountable_sent_at || !!r.member_sent_at || !!r.sepa_batch_id);

/* ── Numéro de facture ── */

const LEGAL_FORMS = new Set([
  'SRL', 'SPRL', 'SPRLU', 'SA', 'NV', 'BV', 'BVBA', 'EBVBA', 'ASBL', 'VZW', 'AISBL', 'IVZW', 'SC', 'SCRL', 'SCRIS', 'SCS', 'SCA',
  'SNC', 'CVBA', 'CV', 'COMMV', 'COMMVA', 'VOF', 'SE', 'GIE', 'EESV', 'SCOMM', 'SAS', 'SARL', 'GMBH', 'LTD', 'BVBASPRL',
]);

/** Majuscules, sans accents, espaces ni ponctuation, 20 caractères au plus. */
export function cleanForNumber(value: string | null | undefined, max = 20): string {
  return (value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
    .replace(/[ØŒÆß]/g, ch => ({ Ø: 'O', Œ: 'OE', Æ: 'AE', ß: 'SS' } as Record<string, string>)[ch] ?? '')
    .replace(/[^A-Z0-9]/g, '').slice(0, max);
}

/** Dénomination sans la forme juridique (SRL, SA, ASBL…). */
export function companyNameForNumber(name: string | null | undefined): string {
  const tokens = (name ?? '').replace(/\(([^)]*)\)/g, ' $1 ').split(/[\s,;/]+/).filter(Boolean);
  const isForm = (t: string) => LEGAL_FORMS.has(t.replace(/\./g, '').toUpperCase());
  const kept = tokens.filter(t => !isForm(t));
  return cleanForNumber((kept.length ? kept : tokens).join(' '));
}

const digits = (v: string | null | undefined) => (v ?? '').replace(/\D/g, '');

/** Début du numéro, avant la vérification d'unicité par la base (collision : -2, -3…). */
export function invoicePrefixBase(input: {
  companyNumber: string | null;
  companyName: string | null;
  groupName: string | null;
  lastName: string | null;
  firstName: string | null;
  hasContact: boolean;
}): { base: string } | { error: string } {
  const bce = digits(input.companyNumber);
  if (bce) {
    const name = companyNameForNumber(input.companyName);
    if (!name) return { error: 'Nom d’entreprise manquant (fiche participant ou fiche CRM) : nécessaire au numéro de facture' };
    return { base: `${name}-${bce.slice(-3)}` };
  }
  if (input.groupName?.trim()) {
    const name = cleanForNumber(input.groupName.replace(/^groupe\s+/i, ''));
    if (!name) return { error: 'Nom du groupe manquant : nécessaire au numéro de facture' };
    return { base: name };
  }
  if (!input.hasContact) return { error: 'Fiche CRM introuvable : nom et prénom nécessaires au numéro de facture' };
  const last = cleanForNumber(input.lastName);
  const initial = cleanForNumber(input.firstName).slice(0, 1);
  if (!last || !initial) return { error: 'Nom et prénom à compléter (séparément) dans la fiche CRM : nécessaires au numéro de facture' };
  return { base: `${last}-${initial}` };
}

/* ── Données ── */

export interface BillingParticipant {
  id: string;
  name: string | null;
  groupe: string | null;
  company_name: string | null;
  company_number: string | null;
  send_email: string | null;
  email: string | null;
  type: string | null;
}

export async function fetchBillingParticipants(items: TodoItem[]): Promise<Map<string, BillingParticipant>> {
  const ids = Array.from(new Set(items.flatMap(i => i.participantIds)));
  const out = new Map<string, BillingParticipant>();
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('participants')
      .select('id, name, groupe, company_name, company_number, send_email, email, type').in('id', ids.slice(i, i + 150));
    if (error) throw error;
    for (const r of (data ?? []) as BillingParticipant[]) out.set(r.id, r);
  }
  return out;
}

export interface BillingState {
  /** Factures déjà émises (ou préparées) des tâches affichées. */
  records: Map<string, BillingInvoice>;
  /** Entités dont le début de numéro est déjà figé. */
  prefixes: Map<string, string>;
}

export async function fetchBillingState(items: TodoItem[]): Promise<BillingState> {
  const refs = Array.from(new Set(items.map(i => i.ref)));
  const records = new Map<string, BillingInvoice>();
  const prefixes = new Map<string, string>();
  for (let i = 0; i < refs.length; i += 100) {
    const chunk = refs.slice(i, i + 100);
    const [inv, pre] = await Promise.all([
      supabase.from('billing_invoices').select('*').in('entity_key', chunk),
      supabase.from('billing_prefixes').select('entity_key, prefix').in('entity_key', chunk),
    ]);
    if (inv.error) throw inv.error;
    if (pre.error) throw pre.error;
    for (const r of (inv.data ?? []) as Record<string, unknown>[]) {
      const row = normalizeInvoice(r);
      records.set(invoiceKey(row.entity_key, row.period), row);
    }
    for (const r of (pre.data ?? []) as { entity_key: string; prefix: string }[]) prefixes.set(r.entity_key, r.prefix);
  }
  return { records, prefixes };
}

export async function fetchSepaBatches(limit = 24): Promise<SepaBatch[]> {
  const { data, error } = await supabase.from('sepa_batches').select('*').order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(r => ({ ...(r as unknown as SepaBatch), total: Number(r.total) }));
}

export async function fetchBatchInvoices(batchIds: string[]): Promise<BillingInvoice[]> {
  if (batchIds.length === 0) return [];
  const { data, error } = await supabase.from('billing_invoices').select('*').in('sepa_batch_id', batchIds).order('invoice_number');
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(normalizeInvoice);
}

/** Total des fichiers de domiciliation dont la date de prélèvement tombe dans le même mois. */
export async function monthCollectedTotal(collectionDate: string): Promise<number> {
  const start = `${collectionDate.slice(0, 7)}-01`;
  const [y, m] = collectionDate.split('-').map(Number);
  const end = `${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`;
  const { data, error } = await supabase.from('sepa_batches').select('total').gte('collection_date', start).lt('collection_date', end);
  if (error) throw error;
  return ((data ?? []) as { total: number | string }[]).reduce((acc, b) => acc + Number(b.total), 0);
}

/* ── Analyse d'une ligne de la todolist ── */

export interface BillingRow {
  item: TodoItem;
  key: string;
  channel: BillingChannel;
  contact: CrmContact | null;
  /** Adresse de facturation de la fiche participant, sinon e-mail du compte, sinon fiche CRM. */
  email: string | null;
  prefix: { base: string } | { error: string };
  /** Motifs qui empêchent la facturation (ligne en rouge). */
  issues: string[];
  record: BillingInvoice | null;
  /** Déjà partie vers une destination : canal figé (celui de la facture). */
  frozen: boolean;
}

export function analyzeBillingItem(
  item: TodoItem,
  contacts: CrmContact[],
  participants: Map<string, BillingParticipant>,
  { records, prefixes }: BillingState,
): BillingRow {
  const contact = item.contactId ? contacts.find(c => c.id === item.contactId) ?? null : null;
  const rows = item.participantIds.map(id => participants.get(id)).filter((p): p is BillingParticipant => !!p);
  const record = records.get(itemInvoiceKey(item)) ?? null;
  const frozen = isStarted(record);
  const channel: BillingChannel = frozen ? record!.channel : (item.billingChannel ?? resolveBillingChannel(contact, item));
  const email = rows.find(r => r.send_email?.trim())?.send_email?.trim()
    ?? rows.find(r => r.email?.trim())?.email?.trim()
    ?? contact?.billing_email?.trim() ?? contact?.email?.trim() ?? null;

  const bce = digits(item.companyNumber);
  const companyName = rows.find(r => r.company_name?.trim() && (!bce || digits(r.company_number) === bce))?.company_name
    ?? (contact?.entity_type === 'morale' && (!contact.company_number || digits(contact.company_number) === bce) ? contact.company_name : null);
  const prefix = invoicePrefixBase({
    companyNumber: item.companyNumber,
    companyName: companyName ?? null,
    groupName: item.ref.startsWith('g:') ? (rows.find(r => r.groupe?.trim())?.groupe ?? contact?.groupe ?? null) : null,
    lastName: contact?.last_name ?? null,
    firstName: contact?.first_name ?? null,
    hasContact: !!contact,
  });

  const issues: string[] = [];
  // Début de numéro déjà figé (facture d'un mois précédent) : le nom actuel n'importe plus.
  if (!record && !prefixes.has(item.ref) && 'error' in prefix) issues.push(prefix.error);
  if (!item.periodStart) issues.push('Période inconnue');
  if (channel === 'peppol') {
    if (!bce) issues.push('Accountable : pas de n° BCE (sociétés uniquement)');
    const copy = frozen ? record!.copy_to_member : !!contact?.billing_copy_to_member;
    if (copy && !email) issues.push('Copie au membre activée sans adresse e-mail');
  } else if (channel === 'email') {
    if (!email) issues.push('Pas d’adresse e-mail de facturation');
  } else if (!frozen) {
    if (!contact) issues.push('Fiche CRM introuvable');
    else {
      if (contact.role !== 'consumer' || contact.entity_type !== 'physique' || bce) {
        issues.push('Domiciliation réservée aux particuliers consommateurs');
      }
      const state = mandateState(contact);
      if (state !== 'active') {
        issues.push(state === 'pending' ? 'Mandat SEPA en attente de signature' : state === 'revoked' ? 'Mandat SEPA révoqué' : 'Pas de mandat SEPA');
      }
      const ibanErr = ibanError(contact.iban);
      if (ibanErr) issues.push(ibanErr === 'IBAN manquant' ? 'Mandat sans IBAN' : ibanErr);
      if (!contact.account_holder?.trim()) issues.push('Titulaire du compte manquant');
      if (!isValidBic(contact.bic)) issues.push('BIC manquant ou invalide');
      if (!contact.mandate_signed_at) issues.push('Date de signature du mandat manquante');
    }
    if (!email) issues.push('Pas d’adresse e-mail (pré-notification)');
  } else if (!email) {
    issues.push('Pas d’adresse e-mail (pré-notification)');
  }
  return { item, key: itemInvoiceKey(item), channel, contact, email, prefix, issues, record, frozen };
}

/* ── Actions ── */

/** Réserve (ou reprend) la facture du mois : numéro figé, canal, dates et mandat tant que rien n'est parti. */
export async function reserveInvoice(row: BillingRow, opts: { issueDate: string; collectionDate: string | null }): Promise<BillingInvoice> {
  const c = row.contact;
  const directDebit = row.channel === 'direct_debit';
  const { data, error } = await supabase.rpc('billing_reserve_invoice', {
    p: {
      entity_key: row.item.ref,
      period: billingPeriodOf(row.item),
      period_start: row.item.periodStart,
      period_end: row.item.periodEnd,
      prefix_base: 'base' in row.prefix ? row.prefix.base : null,
      contact_id: c?.id ?? null,
      sharing_operation_id: row.item.operationId,
      participant_ids: row.item.participantIds,
      company_number: row.item.companyNumber,
      title: row.item.title,
      channel: row.channel,
      issue_date: opts.issueDate,
      collection_date: directDebit ? opts.collectionDate : null,
      member_email: row.email,
      // Peppol : copie au membre selon la fiche ; e-mail et domiciliation : le membre reçoit toujours sa facture.
      copy_to_member: row.channel === 'peppol' ? !!c?.billing_copy_to_member : true,
      mandate: directDebit && c ? {
        reference: c.mandate_reference,
        signed_at: c.mandate_signed_at,
        debtor_name: c.account_holder?.trim() ?? '',
        iban: normalizeIban(c.iban),
        bic: normalizeBic(c.bic) || null,
      } : null,
    },
  });
  if (error) throw error;
  return normalizeInvoice(data as Record<string, unknown>);
}

export async function updateInvoice(id: string, patch: Partial<BillingInvoice>): Promise<BillingInvoice> {
  const { data, error } = await supabase.from('billing_invoices').update(patch).eq('id', id).select().maybeSingle();
  if (error) throw error;
  if (!data) throw await noRowError('billing_invoices', id, 'Facture');
  return normalizeInvoice(data as Record<string, unknown>);
}

/** Numéro et paiement imprimés sur le PDF. */
export function billingInfoFor(record: BillingInvoice, creditorId: string): InvoiceBillingInfo {
  return {
    invoiceNumber: record.invoice_number,
    channel: record.channel,
    collectionDate: record.collection_date,
    maskedIban: record.debtor_iban ? maskIban(record.debtor_iban) : null,
    mandateReference: record.mandate_reference,
    creditorId,
  };
}

export interface CreatedBatch {
  batch: SepaBatch;
  xml: string;
  file_name: string;
  warnings: string[];
}

export async function createSepaBatch(invoiceIds: string[]): Promise<CreatedBatch> {
  return callFunction('create-sepa-batch', { invoice_ids: invoiceIds });
}

export function downloadText(text: string, fileName: string, type = 'application/xml'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Retéléchargement d'un fichier XML archivé. */
export async function downloadSepaFile(batch: SepaBatch): Promise<void> {
  const { data, error } = await supabase.storage.from('sepa-files').download(batch.file_path);
  if (error || !data) throw error ?? new Error('Fichier introuvable');
  downloadText(await data.text(), batch.file_name);
}

export async function setInvoiceStatus(ids: string[], status: BillingStatus, date: string): Promise<void> {
  const { error } = await supabase.from('billing_invoices').update({ status, status_date: date }).in('id', ids);
  if (error) throw error;
}

export const formatEuro = (v: number) => `${v.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
